/**
 * Carrusel dinámico del cart drawer.
 * Si el personalizador no tiene colección, arma venta cruzada con la API
 * de recomendaciones: primero complementarios y después relacionados,
 * tomando hasta 3 productos del carrito y excluyendo los que ya están.
 */

(function() {
  'use strict';

  // Prevenir múltiples inicializaciones
  if (window.CART_RECOMMENDATIONS_INITIALIZED) {
    console.log('🚫 [Cart Recommendations] Ya inicializado, saltando...');
    return;
  }

  window.CART_RECOMMENDATIONS_INITIALIZED = true;
  console.log('🚀 [Cart Recommendations] Inicializando sistema de recomendaciones...');

  // Configuración pasada desde Liquid. Se vuelve a leer en initialize
  // porque el script del drawer puede renderizarse después de este archivo.
  let config = {
    enabled: false,
    type: 'related',
    limit: 8,
    buttonText: 'Añadir al carrito'
  };

  function applyConfig() {
    const source = window.CART_RECOMMENDATIONS_CONFIG || {};
    config.enabled = source.enabled === true || source.enabled === 'true' || source.enabled === 1;
    config.type = (source.type || 'related').toLowerCase().trim();
    config.limit = parseInt(source.limit, 10) || 8;
    config.buttonText = source.buttonText || 'Añadir al carrito';
    if (config.limit < 2) config.limit = 2;
    if (config.limit > 12) config.limit = 12;
  }

  applyConfig();
  
  console.log('🔧 [Cart Recommendations] Configuración:', config);

  /**
   * Obtiene el primer producto del carrito
   */
  async function getFirstCartProduct() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cart = await response.json();
      
      if (!cart.items || cart.items.length === 0) {
        console.log('ℹ️ [Cart Recommendations] Carrito vacío');
        return null;
      }

      const firstItem = cart.items[0];
      console.log('📦 [Cart Recommendations] Primer producto del carrito:', {
        product_id: firstItem.product_id,
        title: firstItem.product_title
      });

      return {
        product_id: firstItem.product_id,
        handle: firstItem.handle,
        title: firstItem.product_title
      };
    } catch (error) {
      console.error('❌ [Cart Recommendations] Error al obtener carrito:', error);
      return null;
    }
  }

  /**
   * Obtiene productos recomendados desde la API de Shopify
   */
  async function getRecommendedProducts(productId, intent = 'related', limit = 5) {
    try {
      const url = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;
      console.log('📡 [Cart Recommendations] Llamando a API:', url);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [Cart Recommendations] Productos recomendados recibidos:', data.products?.length || 0);

      return data.products || [];
    } catch (error) {
      console.error('❌ [Cart Recommendations] Error al obtener recomendaciones:', error);
      return [];
    }
  }

  /**
   * Venta cruzada: complementarios del carrito y, si faltan, relacionados.
   * Recorre hasta 3 productos del carrito y excluye los que ya están adentro.
   */
  async function getCrossSellProducts(limit) {
    const response = await fetch('/cart.js');
    if (!response.ok) return [];
    const cart = await response.json();
    const items = cart.items || [];
    if (!items.length) return [];

    const cartIds = new Set(items.map((item) => item.product_id).filter(Boolean));
    const seeds = items.slice(0, 3);
    const collected = [];
    const seen = new Set();

    function take(products) {
      (products || []).forEach((product) => {
        if (!product || seen.has(product.id) || cartIds.has(product.id)) return;
        if (product.available === false) return;
        seen.add(product.id);
        collected.push(product);
      });
    }

    for (let i = 0; i < seeds.length; i++) {
      if (collected.length >= limit) break;
      const seed = seeds[i];
      const pair = await Promise.all([
        getRecommendedProducts(seed.product_id, 'complementary', 10),
        getRecommendedProducts(seed.product_id, 'related', 10)
      ]);
      take(pair[0]);
      take(pair[1]);
    }

    return collected.slice(0, limit);
  }

  function hideCarouselSection(section) {
    if (!section) return;
    section.hidden = true;
  }

  /**
   * Obtiene IDs de productos ya en el carrito para excluirlos
   */
  async function getCartProductIds() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return [];
      
      const cart = await response.json();
      return cart.items.map(item => item.product_id).filter(Boolean);
    } catch (error) {
      console.error('❌ [Cart Recommendations] Error al obtener IDs del carrito:', error);
      return [];
    }
  }

  /**
   * Obtiene datos completos de un producto desde la API
   */
  async function getProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // DEBUG: Log estructura cruda de la API para el primer producto (evitar spam)
      if (!window._cartRecDebugLogged) {
        window._cartRecDebugLogged = true;
        console.log('🔬 [Cart Recommendations] Estructura cruda de /products/' + handle + '.js:', {
          topLevelKeys: Object.keys(data),
          hasVariants: Array.isArray(data.variants),
          variantsLength: data.variants?.length,
          firstVariant: data.variants?.[0] ? {
            keys: Object.keys(data.variants[0]),
            price: data.variants[0].price,
            compare_at_price: data.variants[0].compare_at_price,
            compareAtPrice: data.variants[0].compareAtPrice
          } : null,
          product_price: data.price,
          product_compare_at_price: data.compare_at_price
        });
      }
      return data;
    } catch (error) {
      console.warn(`⚠️ [Cart Recommendations] Error al obtener datos de ${handle}:`, error);
      return null;
    }
  }

  /**
   * Obtiene el HTML del precio ya renderizado por Liquid (API de secciones).
   * Incluye descuento de colección (metafield numero_de_descuento) y compare_at_price.
   */
  async function getProductPriceHTML(productHandle) {
    try {
      const url = `/products/${encodeURIComponent(productHandle)}?sections=cart-drawer-price-section`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const sectionKey = Object.keys(data).find(k => k.startsWith('cart-drawer-price-section'));
      const sectionHTML = sectionKey ? data[sectionKey] : data['cart-drawer-price-section'];
      if (!sectionHTML) return null;

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const priceWrapper = doc.querySelector('.LOP-product-price-wrapper');
      if (!priceWrapper) return null;

      const priceElement = priceWrapper.querySelector('.product-price');
      const badges = doc.querySelector('.LOP-discount-badges-container');
      return {
        priceHTML: priceElement ? priceElement.outerHTML : priceWrapper.innerHTML,
        badgesHTML: badges ? badges.outerHTML : ''
      };
    } catch (err) {
      console.warn(`[Cart Recommendations] No se pudo obtener precio HTML para ${productHandle}:`, err.message);
      return null;
    }
  }

  /**
   * Formatea dinero en formato argentino
   */
  function formatMoney(cents) {
    const amount = Math.round(cents / 100);
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  function optionNameAt(product, index) {
    const options = product.options || [];
    const option = options[index];
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.name || '';
  }

  function variantOptionValue(variant, index) {
    if (!variant) return '';
    return variant['option' + (index + 1)] || '';
  }

  function findSizeOptionIndex(product) {
    const options = product.options || [];
    for (let i = 0; i < options.length; i++) {
      const name = optionNameAt(product, i).toLowerCase().trim();
      if (name === 'talle' || name === 'talla' || name === 'size' || name === 'tamaño' || name === 'tamano') {
        return i;
      }
    }
    return -1;
  }

  /**
   * Talles con stock del color (u otras opciones) de la variante base.
   * Los agotados no entran al selector, igual que en Kappa.
   */
  function sizeChoicesFor(product, baseVariant) {
    const sizeIndex = findSizeOptionIndex(product);
    if (sizeIndex < 0 || !baseVariant || !product.variants) return [];

    const choices = [];
    const seen = {};
    const optionCount = (product.options || []).length;

    product.variants.forEach(function(variant) {
      if (variant.available === false) return;

      for (let i = 0; i < optionCount; i++) {
        if (i === sizeIndex) continue;
        if (String(variantOptionValue(baseVariant, i)) !== String(variantOptionValue(variant, i))) return;
      }

      const label = String(variantOptionValue(variant, sizeIndex) || '').trim();
      if (!label || seen[label]) return;
      seen[label] = true;
      choices.push({ id: variant.id, label: label });
    });

    return choices;
  }

  function sizeSelectHTML(product, variant) {
    const choices = sizeChoicesFor(product, variant);
    if (!choices.length) return { html: '', variantId: variant && variant.id };

    const selectedChoice = choices.find(function(choice) {
      return String(choice.id) === String(variant.id);
    }) || choices[0];

    const optionsHTML = choices.map(function(choice) {
      const selected = String(choice.id) === String(selectedChoice.id);
      return '<option value="' + choice.id + '" data-available="true"' + (selected ? ' selected' : '') + '>' +
        escapeHtml(choice.label) + '</option>';
    }).join('');

    return {
      html: '<label class="visually-hidden" for="LopCartRecSize-' + product.id + '">Talle</label>' +
        '<select id="LopCartRecSize-' + product.id + '" class="LOP-cart-drawer--carousel-size" data-lop-cart-rec-size aria-label="Talle">' +
        optionsHTML + '</select>',
      variantId: selectedChoice.id
    };
  }

  /**
   * Genera el HTML para un producto del carrusel.
   * priceHTML: opcional, HTML del precio desde API de secciones (Liquid con descuento colección + compare_at).
   */
  function generateProductHTML(product, buttonText = 'Añadir al carrito', sectionPayload = null) {
    const priceHTMLFromSection = sectionPayload && typeof sectionPayload === 'object'
      ? sectionPayload.priceHTML
      : sectionPayload;
    const badgesHTML = sectionPayload && typeof sectionPayload === 'object'
      ? (sectionPayload.badgesHTML || '')
      : '';
    // Manejar imagen
    let imageHTML = '';
    if (product.featured_image) {
      const imageUrl = product.featured_image.startsWith('http') 
        ? product.featured_image 
        : product.featured_image.startsWith('//') 
          ? `https:${product.featured_image}`
          : `${window.location.origin}${product.featured_image}`;
      
      imageHTML = `<img src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy" draggable="false" class="lazyload" style="width: 100%; height: 100%; object-fit: contain;">`;
    } else {
      imageHTML = `<div class="thb-placeholder" style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #f5f5f5;">
        <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <rect width="100" height="100" fill="#f5f5f5"/>
          <text x="50" y="50" text-anchor="middle" fill="#999" font-size="12">Sin imagen</text>
        </svg>
      </div>`;
    }

    // Truncar título
    const title = product.title.length > 24 
      ? product.title.substring(0, 24) + '...' 
      : product.title;

    // Helper: leer precio o compare_at_price (API puede devolver snake_case o camelCase)
    function toCamel(str) {
      return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    }
    function getVariantPrice(v, key) {
      if (!v) return 0;
      const val = v[key] ?? v[toCamel(key)];
      return Math.round(Number(val)) || 0;
    }
    function getProductPrice(p, key) {
      if (!p) return 0;
      const val = p[key] ?? p[toCamel(key)];
      return Math.round(Number(val)) || 0;
    }

    // Buscar la variante con descuento (compare_at_price > price) para mostrar precio con descuento
    const variants = product.variants || [];
    
    // DEBUG: Log estructura del producto para diagnosticar descuentos
    const debugProductKeys = Object.keys(product).filter(k => k.includes('price') || k.includes('compare'));
    console.log(`🔍 [Cart Recommendations] Producto "${product.handle}":`, {
      keysConPrecio: debugProductKeys,
      product_price: product.price,
      product_compare_at_price: product.compare_at_price,
      variants_count: variants.length,
      variantes_precios: variants.slice(0, 5).map((v, i) => ({
        index: i,
        id: v.id,
        price: v.price,
        compare_at_price: v.compare_at_price,
        compareAtPrice: v.compareAtPrice,
        available: v.available,
        priceNum: getVariantPrice(v, 'price'),
        compareAtNum: getVariantPrice(v, 'compare_at_price')
      }))
    });
    
    let variant = variants.find(v => {
      const price = getVariantPrice(v, 'price');
      const compareAt = getVariantPrice(v, 'compare_at_price');
      return (v.available !== false) && compareAt > price && compareAt > 0;
    });
    if (!variant) {
      variant = variants.find(v => v.available !== false) || variants[0];
    }
    // Precios: variante primero, luego producto
    let priceInCents = getVariantPrice(variant, 'price') || getProductPrice(product, 'price');
    let compareAtPriceInCents = getVariantPrice(variant, 'compare_at_price') || getProductPrice(product, 'compare_at_price');
    // Fallback: si la variante no tiene compare_at_price pero el producto sí (aggregate)
    if (compareAtPriceInCents === 0 && priceInCents > 0) {
      const cpMax = getProductPrice(product, 'compare_at_price_max');
      const cpMin = getProductPrice(product, 'compare_at_price_min');
      const cp = getProductPrice(product, 'compare_at_price');
      if (cpMax > priceInCents) compareAtPriceInCents = cpMax;
      else if (cpMin > priceInCents) compareAtPriceInCents = cpMin;
      else if (cp > priceInCents) compareAtPriceInCents = cp;
    }
    const hasDiscount = compareAtPriceInCents > priceInCents && compareAtPriceInCents > 0;
    
    // DEBUG: Log resultado de precios para este producto
    console.log(`💰 [Cart Recommendations] Precios "${product.handle}":`, {
      variantId: variant?.id,
      priceInCents,
      compareAtPriceInCents,
      hasDiscount,
      formattedPrice: formatMoney(priceInCents),
      formattedComparePrice: hasDiscount ? formatMoney(compareAtPriceInCents) : '(sin descuento)'
    });

    // Formatear precios
    const formattedPrice = formatMoney(priceInCents);
    const formattedComparePrice = hasDiscount ? formatMoney(compareAtPriceInCents) : '';

    // Generar HTML de precio: desde API de secciones (Liquid) o fallback con datos de la API .js
    let priceHTML = '';
    if (priceHTMLFromSection && typeof priceHTMLFromSection === 'string' && priceHTMLFromSection.indexOf('product-price') !== -1) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = priceHTMLFromSection;
      const strikeEl = tempDiv.querySelector('.product-price--strike');
      const saleEl = tempDiv.querySelector('.product-price--sale');
      const strikeText = strikeEl ? strikeEl.textContent.trim() : '';
      const saleText = saleEl ? saleEl.textContent.trim() : '';
      let regularText = '';
      const regularEls = tempDiv.querySelectorAll('.product-price--regular');
      for (let i = 0; i < regularEls.length; i++) {
        if (!regularEls[i].classList.contains('product-price--strike')) {
          regularText = regularEls[i].textContent.trim();
          break;
        }
      }
      const finalText = saleText || regularText;
      if (strikeText) {
        priceHTML = `<span class="LOP-cart-drawer--carousel-price-row"><del class="LOP-cart-drawer--carousel-price-compare">${escapeHtml(strikeText)}</del> <ins class="LOP-cart-drawer--carousel-price-final">${escapeHtml(finalText)}</ins></span>`;
      } else {
        priceHTML = `<ins class="LOP-cart-drawer--carousel-price-final">${escapeHtml(finalText)}</ins>`;
      }
    } else if (hasDiscount) {
      priceHTML = `
        <span class="LOP-cart-drawer--carousel-price-row">
          <del class="LOP-cart-drawer--carousel-price-compare">${formattedComparePrice}</del>
          <span class="LOP-cart-drawer--carousel-price-final">${formattedPrice}</span>
        </span>
      `;
    } else {
      priceHTML = `<span class="LOP-cart-drawer--carousel-price-final">${formattedPrice}</span>`;
    }

    // Determinar disponibilidad
    const isAvailable = variant?.available !== false && variant?.id;
    const sizeSelect = isAvailable ? sizeSelectHTML(product, variant) : { html: '', variantId: '' };
    const variantId = sizeSelect.variantId || variant?.id;

    return `
      <div class="LOP-cart-drawer--carousel-item">
        <a href="/products/${product.handle}" class="LOP-cart-drawer--carousel-item-link" draggable="false" title="${escapeHtml(title)}">
          <div class="LOP-cart-drawer--carousel-item-image">
            ${badgesHTML}
            ${imageHTML}
          </div>
          <div class="LOP-cart-drawer--carousel-item-info">
            <span class="LOP-cart-drawer--carousel-item-title">${escapeHtml(title)}</span>
            <div class="LOP-cart-drawer--carousel-item-price">
              ${priceHTML}
            </div>
          </div>
        </a>
        <div class="LOP-cart-drawer--carousel-buy">
          ${sizeSelect.html || ''}
          ${isAvailable ? `
            <button type="button" class="LOP-cart-drawer--carousel-add-btn button small" data-product-id="${variantId}">
              <span class="LOP-cart-drawer--carousel-add-btn-text">${escapeHtml(buttonText)}</span>
            </button>
          ` : `
            <button type="button" class="LOP-cart-drawer--carousel-add-btn button small" disabled>
              <span class="LOP-cart-drawer--carousel-add-btn-text">Agotado</span>
            </button>
          `}
        </div>
      </div>
    `;
  }

  /**
   * Función helper para escapar HTML
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Actualiza el carrusel con productos recomendados
   */
  async function updateRecommendations() {
    console.log('🔄 [Cart Recommendations] ===== INICIANDO ACTUALIZACIÓN =====');

    // Verificar que esté habilitado
    if (!config.enabled) {
      console.log('ℹ️ [Cart Recommendations] Recomendaciones deshabilitadas en metacampos');
      return;
    }

    // Verificar que el cart drawer esté abierto y visible
    const cartDrawer = document.querySelector('#Cart-Drawer.active');
    if (!cartDrawer) {
      console.log('ℹ️ [Cart Recommendations] Cart drawer no está abierto');
      return;
    }

    // Verificar que el cart drawer esté visible (no display: none)
    const drawerComputedStyle = window.getComputedStyle(cartDrawer);
    if (drawerComputedStyle.display === 'none') {
      console.log('⚠️ [Cart Recommendations] Cart drawer está oculto (display: none), esperando...');
      // Esperar hasta que el cart drawer sea visible
      let attempts = 0;
      const checkVisibility = setInterval(() => {
        attempts++;
        const currentDrawer = document.querySelector('#Cart-Drawer.active');
        if (!currentDrawer) {
          clearInterval(checkVisibility);
          console.error('❌ [Cart Recommendations] Cart drawer se cerró mientras esperábamos');
          return;
        }
        const currentStyle = window.getComputedStyle(currentDrawer);
        if (currentStyle.display !== 'none' || attempts > 20) {
          clearInterval(checkVisibility);
          if (currentStyle.display !== 'none') {
            console.log(`✅ [Cart Recommendations] Cart drawer ahora visible, continuando...`);
            // Continuar con la actualización después de un breve delay
            setTimeout(updateRecommendations, 100);
          } else {
            console.error('❌ [Cart Recommendations] Cart drawer sigue oculto después de esperar');
          }
        }
      }, 100);
      return;
    }

    // Verificar que el cart drawer tenga dimensiones
    const drawerWidth = cartDrawer.offsetWidth;
    if (drawerWidth === 0) {
      console.log('⚠️ [Cart Recommendations] Cart drawer tiene ancho 0, esperando a que sea visible...');
      // Esperar hasta que el cart drawer tenga dimensiones
      let attempts = 0;
      const checkVisibility = setInterval(() => {
        attempts++;
        const currentDrawer = document.querySelector('#Cart-Drawer.active');
        if (!currentDrawer) {
          clearInterval(checkVisibility);
          console.error('❌ [Cart Recommendations] Cart drawer se cerró mientras esperábamos');
          return;
        }
        const currentWidth = currentDrawer.offsetWidth;
        if (currentWidth > 0 || attempts > 20) {
          clearInterval(checkVisibility);
          if (currentWidth > 0) {
            console.log(`✅ [Cart Recommendations] Cart drawer ahora visible (${currentWidth}px), continuando...`);
            // Continuar con la actualización después de un breve delay
            setTimeout(updateRecommendations, 100);
          } else {
            console.error('❌ [Cart Recommendations] Cart drawer no se volvió visible después de esperar');
          }
        }
      }, 100);
      return;
    }

    // Buscar TODAS las secciones del carrusel en modo recomendaciones (puede haber duplicados)
    const allCarouselSections = document.querySelectorAll('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]');
    
    // Si hay más de una sección, eliminar las duplicadas (dejar solo la primera)
    if (allCarouselSections.length > 1) {
      console.warn(`⚠️ [Cart Recommendations] Se encontraron ${allCarouselSections.length} secciones de carrusel. Eliminando duplicados...`);
      for (let i = 1; i < allCarouselSections.length; i++) {
        const duplicate = allCarouselSections[i];
        console.log(`🗑️ [Cart Recommendations] Eliminando sección duplicada ${i}`);
        duplicate.remove();
      }
    }
    
    const carouselSection = document.querySelector('#Cart-Drawer.active .LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]')
      || document.querySelector('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]');
    if (!carouselSection) {
      console.log('ℹ️ [Cart Recommendations] No se encontró sección en modo recomendaciones');
      return;
    }

    const carouselTrack = carouselSection.querySelector('.LOP-cart-drawer--carousel-track');
    if (!carouselTrack) {
      console.log('⚠️ [Cart Recommendations] No se encontró track del carrusel');
      return;
    }

    // Declarar carouselContainer al inicio para que esté disponible en todo el scope
    const carouselContainer = carouselSection.querySelector('.LOP-cart-drawer--carousel-container');

    // Mostrar loading
    carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">Cargando recomendaciones...</div>';

    try {
      const filteredProducts = await getCrossSellProducts(Math.max(config.limit, 8));

      if (filteredProducts.length === 0) {
        document.querySelectorAll('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]').forEach(hideCarouselSection);
        return;
      }

      carouselSection.hidden = false;

      // 5. Obtener datos completos de productos (en paralelo, optimizado)
      // Limitar a máximo config.limit + 2 para tener margen
      const productsToFetch = filteredProducts.slice(0, Math.min(config.limit + 2, filteredProducts.length));
      
      // Hacer todas las llamadas en paralelo (más rápido)
      const productDataPromises = productsToFetch.map(product => getProductData(product.handle));
      const productDataResults = await Promise.all(productDataPromises);
      const productsWithData = productDataResults.filter(Boolean);

      console.log(`✅ [Cart Recommendations] Productos con datos completos: ${productsWithData.length}`);

      if (productsWithData.length === 0) {
        hideCarouselSection(carouselSection);
        return;
      }

      const finalProducts = productsWithData.slice(0, config.limit);
      const buttonText = config.buttonText || 'Añadir al carrito';

      // 8. Obtener HTML del precio desde API de secciones (Liquid: descuento colección + compare_at_price)
      const priceHTMLMap = {};
      await Promise.all(finalProducts.map(async (product) => {
        const html = await getProductPriceHTML(product.handle);
        priceHTMLMap[product.handle] = html || null;
      }));

      // 9. Generar HTML de cada producto (con precio desde sección o fallback)
      const productsHTML = finalProducts.map(product =>
        generateProductHTML(product, buttonText, priceHTMLMap[product.handle] || null)
      ).join('');

      console.log(`📝 [Cart Recommendations] HTML generado para ${finalProducts.length} productos (${productsHTML.length} caracteres)`);
      console.log(`🔍 [Cart Recommendations] Primeros 200 caracteres del HTML:`, productsHTML.substring(0, 200));

      // 10. Verificar que el carouselTrack todavía existe (puede haberse regenerado)
      const currentCarouselTrack = carouselSection.querySelector('.LOP-cart-drawer--carousel-track');
      if (!currentCarouselTrack) {
        console.error('❌ [Cart Recommendations] El carouselTrack ya no existe, buscando de nuevo...');
        const newCarouselSection = document.querySelector('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]');
        if (newCarouselSection) {
          const newCarouselTrack = newCarouselSection.querySelector('.LOP-cart-drawer--carousel-track');
          if (newCarouselTrack) {
            console.log('✅ [Cart Recommendations] CarouselTrack encontrado de nuevo, insertando HTML...');
            document.querySelectorAll('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]').forEach((section) => {
              const track = section.querySelector('.LOP-cart-drawer--carousel-track');
              if (track) track.innerHTML = productsHTML;
              section.hidden = false;
            });
            // Reinicializar con el nuevo elemento
            if (window.LOP && typeof window.LOP.initCartCarousel === 'function') {
              setTimeout(() => {
                console.log('🔄 [Cart Recommendations] Reinicializando carrusel...');
                window.LOP.initCartCarousel();
              }, 200);
            }
            console.log('✅ [Cart Recommendations] ===== ACTUALIZACIÓN COMPLETADA =====');
            return;
          }
        }
        console.error('❌ [Cart Recommendations] No se pudo encontrar el carouselTrack');
        return;
      }

      // 11. Insertar HTML
      document.querySelectorAll('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]').forEach((section) => {
        const track = section.querySelector('.LOP-cart-drawer--carousel-track');
        if (track) track.innerHTML = productsHTML;
        section.hidden = false;
      });
      console.log(`✅ [Cart Recommendations] HTML insertado en carouselTrack. Elementos hijos: ${currentCarouselTrack.children.length}`);
      
      // Verificar que los items se insertaron correctamente
      const insertedItems = currentCarouselTrack.querySelectorAll('.LOP-cart-drawer--carousel-item');
      console.log(`🔍 [Cart Recommendations] Items insertados verificados: ${insertedItems.length}`);
      
      if (insertedItems.length === 0) {
        console.error('❌ [Cart Recommendations] Los items no se insertaron correctamente');
        return;
      }
      
      // Verificar y forzar dimensiones si es necesario (optimizado - solo si es necesario)
      if (carouselContainer && carouselContainer.offsetWidth === 0) {
        // Solo verificar si el contenedor tiene ancho 0
        const scrollWrapper = cartDrawer.querySelector('.LOP-cart-drawer--scroll-wrapper');
        if (scrollWrapper && scrollWrapper.offsetWidth > 0) {
          const wrapperWidth = scrollWrapper.offsetWidth;
          console.log(`📐 [Cart Recommendations] Scroll wrapper width: ${wrapperWidth}px, forzando dimensiones...`);
          
          // Verificar el estado de visibilidad de la sección
          const sectionComputedStyle = window.getComputedStyle(carouselSection);
          const containerComputedStyle = window.getComputedStyle(carouselContainer);
          
          // Si el scroll wrapper tiene ancho pero la sección no, forzar el ancho
          if (wrapperWidth > 0 && carouselSection.offsetWidth === 0) {
            console.warn('⚠️ [Cart Recommendations] Scroll wrapper tiene ancho pero la sección no. Forzando ancho...');
            
            // Asegurar que la sección esté visible y en el flujo
            carouselSection.style.display = 'block';
            carouselSection.style.visibility = 'visible';
            carouselSection.style.position = 'relative';
            carouselSection.style.width = `${wrapperWidth}px`;
            carouselSection.style.minWidth = `${wrapperWidth}px`;
            carouselSection.style.maxWidth = `${wrapperWidth}px`;
            carouselSection.style.flexShrink = '0';
            
            // Asegurar que el contenedor también esté visible
            carouselContainer.style.display = 'block';
            carouselContainer.style.visibility = 'visible';
            carouselContainer.style.width = `${wrapperWidth}px`;
            carouselContainer.style.minWidth = `${wrapperWidth}px`;
            carouselContainer.style.maxWidth = `${wrapperWidth}px`;
            
            // Forzar reflow múltiple
            void carouselSection.offsetWidth;
            void carouselContainer.offsetWidth;
            void currentCarouselTrack.offsetWidth;
            
            // Verificar después del reflow
            const sectionWidthAfter = carouselSection.offsetWidth;
            const containerWidthAfter = carouselContainer.offsetWidth;
            
            console.log(`✅ [Cart Recommendations] Ancho forzado - Section: ${sectionWidthAfter}px, Container: ${containerWidthAfter}px`);
            
            // Si todavía es 0, hay un problema más profundo
            if (sectionWidthAfter === 0) {
              console.error('❌ [Cart Recommendations] El ancho sigue siendo 0 después de forzar. Verificando parent y estructura...');
              const parent = carouselSection.parentElement;
              if (parent) {
                const parentComputed = window.getComputedStyle(parent);
                console.log(`🔍 [Cart Recommendations] Parent element:`, {
                  tagName: parent.tagName,
                  className: parent.className,
                  offsetWidth: parent.offsetWidth,
                  clientWidth: parent.clientWidth,
                  scrollWidth: parent.scrollWidth,
                  computedDisplay: parentComputed.display,
                  computedWidth: parentComputed.width,
                  computedFlexDirection: parentComputed.flexDirection,
                  computedOverflow: parentComputed.overflow
                });
                
                // Verificar todos los ancestros
                let current = parent;
                let level = 1;
                while (current && level <= 5) {
                  const computed = window.getComputedStyle(current);
                  console.log(`🔍 [Cart Recommendations] Ancestro nivel ${level}:`, {
                    tagName: current.tagName,
                    className: current.className,
                    offsetWidth: current.offsetWidth,
                    computedDisplay: computed.display,
                    computedWidth: computed.width
                  });
                  current = current.parentElement;
                  level++;
                }
              }
              
              // Intentar una solución más agresiva: verificar y mover la sección si es necesario
              if (scrollWrapper && scrollWrapper.offsetWidth > 0) {
                console.warn('⚠️ [Cart Recommendations] Intentando solución agresiva: verificar posición en DOM...');
                // Verificar si la sección está realmente dentro del scroll-wrapper
                if (!scrollWrapper.contains(carouselSection)) {
                  console.error('❌ [Cart Recommendations] La sección NO está dentro del scroll-wrapper! Moviendo la sección...');
                  
                  // ANTES de mover, verificar que no haya otra sección ya dentro del scroll-wrapper
                  const existingSection = scrollWrapper.querySelector('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]');
                  if (existingSection && existingSection !== carouselSection) {
                    console.warn('⚠️ [Cart Recommendations] Ya existe una sección dentro del scroll-wrapper. Eliminando la sección duplicada...');
                    carouselSection.remove();
                    return; // Salir, ya hay una sección en el lugar correcto
                  }
                  
                  // La sección no está en el lugar correcto, moverla al final del scroll-wrapper
                  scrollWrapper.appendChild(carouselSection);
                  console.log('✅ [Cart Recommendations] Sección movida al scroll-wrapper');
                  
                  // Esperar un momento para que el DOM se actualice
                  setTimeout(() => {
                    // Forzar el ancho después de mover
                    carouselSection.style.setProperty('width', `${wrapperWidth}px`, 'important');
                    carouselSection.style.setProperty('min-width', `${wrapperWidth}px`, 'important');
                    carouselSection.style.setProperty('display', 'block', 'important');
                    carouselSection.style.setProperty('visibility', 'visible', 'important');
                    
                    if (carouselContainer) {
                      carouselContainer.style.setProperty('width', `${wrapperWidth}px`, 'important');
                      carouselContainer.style.setProperty('min-width', `${wrapperWidth}px`, 'important');
                      carouselContainer.style.setProperty('display', 'block', 'important');
                    }
                    
                    // Forzar reflow
                    void carouselSection.offsetWidth;
                    if (carouselContainer) {
                      void carouselContainer.offsetWidth;
                    }
                    
                    const finalWidth = carouselSection.offsetWidth;
                    console.log(`🔧 [Cart Recommendations] Después de mover y usar !important: ${finalWidth}px`);
                  }, 50);
                } else {
                  console.log('✅ [Cart Recommendations] La sección SÍ está dentro del scroll-wrapper');
                  // Verificar si hay otras secciones duplicadas fuera del scroll-wrapper y eliminarlas
                  const allSections = document.querySelectorAll('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]');
                  if (allSections.length > 1) {
                    console.warn(`⚠️ [Cart Recommendations] Se encontraron ${allSections.length} secciones. Eliminando duplicados fuera del scroll-wrapper...`);
                    allSections.forEach((section, index) => {
                      if (section !== carouselSection && !scrollWrapper.contains(section)) {
                        console.log(`🗑️ [Cart Recommendations] Eliminando sección duplicada ${index}`);
                        section.remove();
                      }
                    });
                  }
                  
                  // Intentar forzar el ancho usando !important
                  carouselSection.style.setProperty('width', `${wrapperWidth}px`, 'important');
                  carouselSection.style.setProperty('min-width', `${wrapperWidth}px`, 'important');
                  carouselSection.style.setProperty('display', 'block', 'important');
                  carouselSection.style.setProperty('visibility', 'visible', 'important');
                  
                  if (carouselContainer) {
                    carouselContainer.style.setProperty('width', `${wrapperWidth}px`, 'important');
                    carouselContainer.style.setProperty('min-width', `${wrapperWidth}px`, 'important');
                    carouselContainer.style.setProperty('display', 'block', 'important');
                  }
                  
                  // Forzar reflow
                  void carouselSection.offsetWidth;
                  if (carouselContainer) {
                    void carouselContainer.offsetWidth;
                  }
                  
                  const finalWidth = carouselSection.offsetWidth;
                  console.log(`🔧 [Cart Recommendations] Después de usar !important: ${finalWidth}px`);
                }
              }
            }
          }
        }
        
        console.log(`📐 [Cart Recommendations] Dimensiones después de insertar - Section: ${carouselSection.offsetWidth}px, Container: ${carouselContainer.offsetWidth}px`);
      }

      // 10. Forzar visibilidad y recálculo de dimensiones
      // carouselContainer ya está declarado arriba, reutilizarlo
      currentCarouselTrack.style.display = 'flex';
      currentCarouselTrack.style.visibility = 'visible';
      currentCarouselTrack.style.opacity = '1';
      
      // Asegurar que la sección y el contenedor estén visibles
      carouselSection.style.display = 'block';
      carouselSection.style.visibility = 'visible';
      carouselSection.style.opacity = '1';
      
      if (carouselContainer) {
        carouselContainer.style.display = 'block';
        carouselContainer.style.visibility = 'visible';
        carouselContainer.style.opacity = '1';
      }
      
      // Forzar recálculo de layout
      void currentCarouselTrack.offsetWidth;
      void carouselSection.offsetWidth;
      if (carouselContainer) {
        void carouselContainer.offsetWidth;
      }
      
      // 11. Reinicializar carrusel con delay más largo para asegurar que el DOM esté listo
      if (window.LOP && typeof window.LOP.initCartCarousel === 'function') {
        setTimeout(() => {
          console.log('🔄 [Cart Recommendations] Reinicializando carrusel...');
          
          // Verificar que los items estén en el DOM antes de inicializar
          const itemsBeforeInit = currentCarouselTrack.querySelectorAll('.LOP-cart-drawer--carousel-item');
          console.log(`🔍 [Cart Recommendations] Items antes de reinicializar: ${itemsBeforeInit.length}`);
          
          if (itemsBeforeInit.length > 0) {
            // Verificar y forzar dimensiones si es necesario
            // carouselContainer ya está declarado arriba, reutilizarlo
            if (carouselContainer) {
              // Forzar reflow para que el navegador calcule las dimensiones
              void carouselSection.offsetWidth;
              void carouselContainer.offsetWidth;
              void currentCarouselTrack.offsetWidth;
              
              console.log(`📐 [Cart Recommendations] Antes de inicializar - Section: ${carouselSection.offsetWidth}px, Container: ${carouselContainer.offsetWidth}px, Track: ${currentCarouselTrack.offsetWidth}px`);
              
              // Si el contenedor todavía tiene ancho 0, forzar el ancho basado en el scroll wrapper
              if (carouselContainer.offsetWidth === 0) {
                console.warn('⚠️ [Cart Recommendations] El contenedor todavía tiene ancho 0. Forzando ancho basado en scroll wrapper...');
                const scrollWrapper = cartDrawer.querySelector('.LOP-cart-drawer--scroll-wrapper');
                const drawerContainer = cartDrawer.querySelector('.LOP-cart-drawer--container');
                
                console.log(`📐 [Cart Recommendations] Estructura - Drawer: ${cartDrawer.offsetWidth}px, Drawer Container: ${drawerContainer?.offsetWidth || 0}px, Scroll Wrapper: ${scrollWrapper?.offsetWidth || 0}px`);
                
                // Usar el ancho del scroll wrapper si está disponible
                if (scrollWrapper && scrollWrapper.offsetWidth > 0) {
                  const targetWidth = scrollWrapper.offsetWidth;
                  carouselSection.style.width = `${targetWidth}px`;
                  carouselSection.style.minWidth = `${targetWidth}px`;
                  carouselContainer.style.width = '100%';
                  carouselContainer.style.minWidth = '100%';
                  
                  // Forzar reflow
                  void carouselSection.offsetWidth;
                  void carouselContainer.offsetWidth;
                  
                  console.log(`✅ [Cart Recommendations] Ancho forzado basado en scroll wrapper: ${targetWidth}px`);
                } else if (drawerContainer && drawerContainer.offsetWidth > 0) {
                  // Fallback: usar el ancho del drawer container
                  const targetWidth = drawerContainer.offsetWidth;
                  carouselSection.style.width = `${targetWidth}px`;
                  carouselSection.style.minWidth = `${targetWidth}px`;
                  carouselContainer.style.width = '100%';
                  carouselContainer.style.minWidth = '100%';
                  
                  // Forzar reflow
                  void carouselSection.offsetWidth;
                  void carouselContainer.offsetWidth;
                  
                  console.log(`✅ [Cart Recommendations] Ancho forzado basado en drawer container: ${targetWidth}px`);
                }
              }
            }
            
            // NO llamar a initCartCarousel del script nativo para evitar duplicación
            // En su lugar, inicializar la navegación del carrusel de recomendaciones
            const prevButton = carouselSection.querySelector('.LOP-cart-drawer--carousel-prev');
            const nextButton = carouselSection.querySelector('.LOP-cart-drawer--carousel-next');
            
            // Función para actualizar el estado de los botones
            const updateNavButtons = () => {
              if (!carouselContainer) return;
              const scrollLeft = carouselContainer.scrollLeft;
              const scrollWidth = carouselContainer.scrollWidth;
              const clientWidth = carouselContainer.clientWidth;
              const maxScroll = scrollWidth - clientWidth;
              
              if (prevButton) prevButton.disabled = scrollLeft <= 0;
              if (nextButton) nextButton.disabled = scrollLeft >= maxScroll - 1;
            };
            
            // Configurar botones de navegación
            if (prevButton) {
              prevButton.addEventListener('click', () => {
                if (!carouselContainer) return;
                const itemWidth = itemsBeforeInit[0]?.offsetWidth || 177;
                carouselContainer.scrollBy({ left: -itemWidth * 3, behavior: 'smooth' });
              });
            }
            
            if (nextButton) {
              nextButton.addEventListener('click', () => {
                if (!carouselContainer) return;
                const itemWidth = itemsBeforeInit[0]?.offsetWidth || 177;
                carouselContainer.scrollBy({ left: itemWidth * 3, behavior: 'smooth' });
              });
            }
            
            // Actualizar botones cuando se hace scroll
            if (carouselContainer) {
              carouselContainer.addEventListener('scroll', updateNavButtons);
              updateNavButtons(); // Estado inicial
              
              // Prevenir scroll horizontal con rueda del mouse en desktop
              if (window.innerWidth >= 768) {
                carouselContainer.addEventListener('wheel', (e) => {
                  // Si el scroll es principalmente horizontal, prevenirlo
                  if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  // Si el scroll es vertical, permitirlo (para el scroll del drawer)
                  // pero no propagarlo al contenedor del carrusel
                  if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                    // Permitir scroll vertical pero no afectar el carrusel
                    return;
                  }
                }, { passive: false });
              }
            }
            
            // Verificar después de inicializar
            setTimeout(() => {
              const itemsAfterInit = currentCarouselTrack.querySelectorAll('.LOP-cart-drawer--carousel-item');
              const container = carouselSection.querySelector('.LOP-cart-drawer--carousel-container');
              
              console.log(`🔍 [Cart Recommendations] Items en carrusel después de reinicializar: ${itemsAfterInit.length}`);
              
              if (container) {
                // Esperar un frame más para que el layout se calcule
                requestAnimationFrame(() => {
                  // Verificar dimensiones
                  const containerWidth = container.clientWidth;
                  const containerScrollWidth = container.scrollWidth;
                  const trackWidth = currentCarouselTrack.scrollWidth;
                  const itemWidth = itemsAfterInit[0]?.offsetWidth || 0;
                  const maxScroll = containerScrollWidth - containerWidth;
                  
                  console.log(`📏 [Cart Recommendations] Container: ${containerWidth}px, ScrollWidth: ${containerScrollWidth}px`);
                  console.log(`📏 [Cart Recommendations] Track width: ${trackWidth}px, Item width: ${itemWidth}px, MaxScroll: ${maxScroll}px`);
                  
                  // Si el contenedor tiene ancho 0, hay un problema con el CSS o el timing
                  if (containerWidth === 0 && itemsAfterInit.length > 0) {
                    console.error('❌ [Cart Recommendations] Container tiene ancho 0. Esto indica un problema con el CSS o el timing.');
                    console.error('❌ [Cart Recommendations] El CSS debería dar width: 100% al contenedor, pero no se está aplicando.');
                    
                    // Diagnosticar el problema
                    const scrollWrapper = cartDrawer.querySelector('.LOP-cart-drawer--scroll-wrapper');
                    const drawerContainer = cartDrawer.querySelector('.LOP-cart-drawer--container');
                    
                    console.log(`🔍 [Cart Recommendations] Diagnóstico:`);
                    console.log(`  - Cart Drawer width: ${cartDrawer.offsetWidth}px`);
                    console.log(`  - Drawer Container width: ${drawerContainer?.offsetWidth || 0}px`);
                    console.log(`  - Scroll Wrapper width: ${scrollWrapper?.offsetWidth || 0}px`);
                    console.log(`  - Carousel Section width: ${carouselSection.offsetWidth}px`);
                    console.log(`  - Carousel Container width: ${container.clientWidth}px`);
                    console.log(`  - Carousel Container computed style:`, window.getComputedStyle(container).width);
                    
                    // Si el scroll wrapper tiene ancho pero el contenedor no, hay un problema de CSS
                    if (scrollWrapper && scrollWrapper.offsetWidth > 0) {
                      console.warn('⚠️ [Cart Recommendations] El scroll wrapper tiene ancho pero el contenedor no. Verificando CSS...');
                      // El CSS debería manejar esto, pero si no funciona, hay un problema
                    }
                  } else if (maxScroll === 0 && itemsAfterInit.length > 0 && trackWidth > containerWidth) {
                    console.warn('⚠️ [Cart Recommendations] MaxScroll es 0 pero el track es más ancho. Forzando recálculo...');
                    // Forzar recálculo forzando reflow
                    container.style.overflow = 'hidden';
                    void container.offsetWidth;
                    container.style.overflow = 'auto';
                    void container.offsetWidth;
                    
                    // Recalcular estado de botones después del recálculo
                    setTimeout(() => {
                      if (carouselContainer) {
                        const updateNavButtons = () => {
                          const scrollLeft = carouselContainer.scrollLeft;
                          const scrollWidth = carouselContainer.scrollWidth;
                          const clientWidth = carouselContainer.clientWidth;
                          const maxScroll = scrollWidth - clientWidth;
                          const prevButton = carouselSection.querySelector('.LOP-cart-drawer--carousel-prev');
                          const nextButton = carouselSection.querySelector('.LOP-cart-drawer--carousel-next');
                          if (prevButton) prevButton.disabled = scrollLeft <= 0;
                          if (nextButton) nextButton.disabled = scrollLeft >= maxScroll - 1;
                        };
                        updateNavButtons();
                      }
                    }, 100);
                  }
                });
              }
              
              if (itemsAfterInit.length === 0) {
                console.warn('⚠️ [Cart Recommendations] El carrusel se reinicializó pero no hay items visibles');
              } else {
                // Verificar visibilidad de los items
                const firstItem = itemsAfterInit[0];
                if (firstItem) {
                  const rect = firstItem.getBoundingClientRect();
                  console.log(`👁️ [Cart Recommendations] Primer item visible: ${rect.width > 0 && rect.height > 0 ? 'Sí' : 'No'} (${rect.width}x${rect.height}px)`);
                }
              }
            }, 200);
          } else {
            console.error('❌ [Cart Recommendations] No hay items en el DOM antes de inicializar el carrusel');
          }
        }, 100);
      }

      console.log('✅ [Cart Recommendations] ===== ACTUALIZACIÓN COMPLETADA =====');
    } catch (error) {
      console.error('❌ [Cart Recommendations] Error al actualizar recomendaciones:', error);
      hideCarouselSection(carouselSection);
    }
  }

  /**
   * Inicializa el sistema de recomendaciones
   */
  function initialize() {
    applyConfig();
    console.log('🔧 [Cart Recommendations] Configurando listeners...');

    // Solo inicializar si está habilitado
    if (!config.enabled) {
      console.log('ℹ️ [Cart Recommendations] Recomendaciones deshabilitadas, no inicializando');
      return;
    }

    let isUpdating = false;
    let lastUpdateTime = 0;
    const UPDATE_DEBOUNCE = 300;

    // Función para actualizar con debounce
    function triggerUpdate() {
      const now = Date.now();
      if (isUpdating || (now - lastUpdateTime < UPDATE_DEBOUNCE)) {
        return;
      }
      lastUpdateTime = now;
      isUpdating = true;

      updateRecommendations().finally(() => {
        isUpdating = false;
      });
    }

    // Estrategia 1: Interceptar openCartDrawer
    const originalOpenCartDrawer = window.LOP?.openCartDrawer || window.openCartDrawer;
    if (originalOpenCartDrawer) {
      const newOpenCartDrawer = function() {
        const result = originalOpenCartDrawer.apply(this, arguments);
        // Esperar a que el cart drawer se abra (reducido para renderizado más rápido)
        setTimeout(() => {
          console.log('🔄 [Cart Recommendations] Cart drawer abierto, actualizando recomendaciones...');
          triggerUpdate();
        }, 400);
        return result;
      };

      if (window.LOP) {
        window.LOP.openCartDrawer = newOpenCartDrawer;
      }
      if (window.openCartDrawer) {
        window.openCartDrawer = newOpenCartDrawer;
      }
    }

    // Estrategia 2: MutationObserver
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.id === 'Cart-Drawer' && target.classList.contains('active')) {
            console.log('🎯 [Cart Recommendations] Cart drawer activado');
            // Esperar a que la regeneración termine (reducido)
            setTimeout(triggerUpdate, 400);
          }
        }

        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              const cartDrawer = node.id === 'Cart-Drawer' 
                ? node 
                : node.querySelector?.('#Cart-Drawer');

              if (cartDrawer && cartDrawer.classList.contains('active')) {
                console.log('🎯 [Cart Recommendations] Cart drawer detectado en DOM');
                triggerUpdate();
              }

              if (node.classList?.contains('LOP-cart-drawer--carousel-section') && 
                  node.dataset.recommendationsMode === 'true') {
                console.log('🎯 [Cart Recommendations] Sección de recomendaciones detectada');
                triggerUpdate();
              }
            }
          });
        }
      });
    });

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Estrategia 3: Interceptar rebuildCartDrawerFromAPI
    const originalRebuild = window.LOP?.rebuildCartDrawerFromAPI;
    if (originalRebuild) {
      window.LOP.rebuildCartDrawerFromAPI = async function(...args) {
        const result = await originalRebuild.apply(this, args);
        // Esperar a que la regeneración termine (reducido)
        setTimeout(() => {
          console.log('🔄 [Cart Recommendations] Cart drawer regenerado, actualizando recomendaciones...');
          triggerUpdate();
        }, 400);
        return result;
      };
    }

    // Estrategia 4: Verificar periódicamente
    const checkInterval = setInterval(() => {
      const carouselSection = document.querySelector('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]');
      const cartDrawer = document.querySelector('#Cart-Drawer.active');

      if (carouselSection && cartDrawer) {
        const track = carouselSection.querySelector('.LOP-cart-drawer--carousel-track');
        if (track) {
          const hasLoading = track.querySelector('.LOP-cart-drawer--carousel-loading');
          if (hasLoading && !track.dataset.recommendationsUpdated) {
            console.log('🎯 [Cart Recommendations] Loading detectado, actualizando...');
            track.dataset.recommendationsUpdated = 'true';
            triggerUpdate();
          }
        }
      }
    }, 1000);

    // Limpiar intervalo después de 10 segundos
    setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);

    // Exponer función globalmente
    window.LOP = window.LOP || {};
    window.LOP.updateRecommendations = updateRecommendations;
    window.LOP.triggerRecommendationsUpdate = triggerUpdate;

    // Función de test
    window.testRecommendations = function() {
      console.log('🧪 [Cart Recommendations] TEST MANUAL - Forzando actualización...');
      updateRecommendations();
    };

    console.log('✅ [Cart Recommendations] Sistema inicializado');
  }

  // Inicializar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

})();
